import React, { useEffect, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Grid
} from "@mui/material";
import { useNotifications } from "../../../hooks/NotificationProvider";
import SubjectModel, { SubjectData, defaultSubjectData, subjectSchema } from "../../../core/api/manager/SubjectModel";
import { translations } from "../../../translations/common";

interface Props {
    subjectId?: string | null;
    close(): void;
}

const SubjectModalForm: React.FC<Props> = ({ subjectId, close }) => {
    const notifications = useNotifications();
    const [value, setValue] = useState<SubjectData>(defaultSubjectData);

    useEffect(() => {
        if (subjectId) {
            SubjectModel.findOne(subjectId)
                .then((subject) => setValue(subject));
        }
    }, [subjectId]);

    return (
        <Dialog
            fullWidth
            maxWidth={"md"}
            open={true}
            onClose={close}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">{translations.subjects.form.title}</DialogTitle>
            <Formik
                initialValues={value}
                enableReinitialize={true}
                onSubmit={(values, actions) => {
                    SubjectModel.updateOrCreate(values)
                        .then(close)
                        .then(() =>
                            notifications.notify({
                                level: "success",
                                message: translations.subjects.form.notifications.success.message,
                            })
                        )
                        .catch((error) => {
                            const data = error.response.data;
                            if (data.errors) {
                                data.errors.forEach((element: any) => {
                                    actions.setFieldError(element.field, element.defaultMessage);
                                });
                            }
                        })
                        .finally(() => {
                            actions.setSubmitting(false);
                        });
                }}
                validationSchema={subjectSchema}
            >
                {(props: FormikProps<SubjectData>) => {
                    const {
                        values,
                        handleBlur,
                        handleChange,
                        isSubmitting,
                    } = props;
                    return (
                        <Form>
                            <DialogContent>
                                <Grid
                                    container
                                    justifyContent="space-around"
                                    direction="row"
                                    spacing={2}
                                >
                                    <Grid
                                        size={{
                                            lg: 10,
                                            md: 10,
                                            sm: 10,
                                            xs: 10
                                        }}>
                                        <TextField
                                            name="subject"
                                            id="subject"
                                            label={translations.subjects.form.fields.subject}
                                            value={values.subject}
                                            type="text"
                                            multiline
                                            fullWidth
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    type="button"
                                    className="outline"
                                    onClick={close}
                                    disabled={isSubmitting}
                                >
                                    {translations.subjects.form.buttons.cancel}
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    color="primary"
                                >
                                    {translations.subjects.form.buttons.save}
                                </Button>
                            </DialogActions>
                        </Form>
                    );
                }}
            </Formik>
        </Dialog>
    );
};

export default SubjectModalForm;
